/*
  Tema claro / oscuro del DSP.

  Cómo funciona:
  - Los tokens NEUTROS de `Colors.js` (fondos, superficies, textos, bordes) no son
    hex fijos sino variables CSS `var(--dsp-*)`. Sus valores light salen del propio
    `Colors.js` (idénticos a los de siempre) y los dark de `DARK_NEUTRALS` (abajo).
  - `applyTheme()` escribe esas variables en `<html>` y setea `data-theme`, que usa
    `darkMode.css` para ajustar Ant Design y clases propias. No hace falta recargar.
  - La preferencia se guarda en localStorage (`dsp.theme`). Más adelante se puede
    sincronizar con la base de datos leyendo/escribiendo el mismo valor.
  - Los colores de marca (primary, secondary, etc.) NO cambian entre temas: el
    `primary` sigue siendo el de la marca en ambos. Lo que sí se deriva de él son
    dos tokens de legibilidad (ver más abajo): `primaryAccent` y `onPrimary`.
*/

export const THEME_STORAGE_KEY = 'dsp.theme';
export const THEME_LIGHT = 'light';
export const THEME_DARK = 'dark';
export const THEME_CHANGE_EVENT = 'dsp:themechange';

// Tokens de Colors.js que cambian con el tema (nombre de token -> variable CSS).
export const THEMED_TOKENS = [
  'background',
  'snow',
  'gray',
  'grayMedium',
  'black',
  'coal',
  'extraLightPrimary',
  'border',
  'borderStrong',
  'surfaceMuted',
  'textStrong',
  'textSecondary',
  'textMuted',
  'overlay',
  'tintWarning',
  'tintSuccess',
  'tintInfo',
  'tintError',
  // Derivados del primary de la marca (ver deriveBrandTokens)
  'primaryAccent',
  'onPrimary'
];

/*
  ---- Tokens derivados del color de marca ----

  `primaryAccent`: el color de marca para TEXTO, iconos y bordes sobre el fondo de
  la página. Es el `primary` tal cual mientras se lea bien; si no, se aclara (tema
  oscuro) u oscurece (tema claro) lo mínimo necesario. Así marcas de primary muy
  oscuro (blaster #042940, laMediática #100c91) siguen siendo legibles en oscuro, y
  marcas de primary muy claro (yoda #ffcc00) lo son en claro. Los rellenos siguen
  usando `primary`, con lo cual la identidad de marca no cambia.

  `onPrimary`: la tinta para texto/iconos ENCIMA de un relleno `primary`. Blanco
  cuando el primary es oscuro (la mayoría) y casi negro cuando es claro (yoda,
  smartFit, territorioDigital), donde el blanco daba 1.5:1.
*/

const TARGET_CONTRAST = 4.5; // AA para texto normal

/*
  Umbrales de disparo: solo se corrige lo que está realmente mal, no todo lo que
  no llega a 4.5. Así la mayoría de las marcas queda EXACTAMENTE como estaba.

  - En oscuro el disparo es el propio 4.5: con la paleta oscura solo quedan por
    debajo los primary muy oscuros (duoPrint, hakMedia, blaster, laMediática).
  - En claro el disparo es 2.5: deja intactos los azules/naranjas (latinad 3.07,
    grupoVia 2.75, mediaShake 2.74, movImagen 2.89, dv360 3.06) y corrige solo los
    amarillos ilegibles sobre blanco (yoda 1.51, smartFit 1.76, territorioD. 2.02).
  - `onPrimary` se vuelve tinta oscura con el mismo criterio: solo cuando el blanco
    sobre el relleno de marca baja de 2.5 (los tres amarillos).
*/
const TRIGGER_ON_DARK = 4.5;
const TRIGGER_ON_LIGHT = 2.5;
const TRIGGER_ON_PRIMARY = 2.5;

const hexToRgb = hex => {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6,8}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
};

const rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');

const relLuminance = ({ r, g, b }) => {
  const f = v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

const contrast = (a, b) => {
  const l1 = relLuminance(a);
  const l2 = relLuminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

const WHITE_RGB = { r: 255, g: 255, b: 255 };
const BLACK_RGB = { r: 17, g: 17, b: 17 };

// RGB <-> HSL: el ajuste se hace moviendo SOLO la luminosidad, así el tono y la
// saturación de la marca se conservan (mezclar hacia blanco desatura y el azul de
// blaster terminaba gris).
const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  if (s === 0) return { r: l * 255, g: l * 255, b: l * 255 };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = t => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 };
};

// Sube o baja la luminosidad de `hex` hasta contrastar `target` contra `bg`.
// Si ya llega a `trigger`, devuelve el color original sin tocarlo.
const adjustForContrast = (hex, bgHex, target = TARGET_CONTRAST, trigger = target) => {
  const rgb = hexToRgb(hex);
  const bg = hexToRgb(bgHex);
  if (!rgb || !bg) return hex;
  if (contrast(rgb, bg) >= trigger) return hex;
  const hsl = rgbToHsl(rgb);
  const up = relLuminance(bg) < 0.5; // fondo oscuro -> aclarar; claro -> oscurecer
  for (let step = 1; step <= 100; step++) {
    const l = up ? hsl.l + step / 100 : hsl.l - step / 100;
    if (l <= 0 || l >= 1) break;
    const candidate = hslToRgb({ ...hsl, l });
    if (contrast(candidate, bg) >= target) return rgbToHex(candidate);
  }
  return rgbToHex(up ? WHITE_RGB : BLACK_RGB);
};

// Tokens derivados para un primary de marca y un fondo de página dados.
export const deriveBrandTokens = (primary, backgroundHex, isDark) => {
  const rgb = hexToRgb(primary);
  if (!rgb) return { primaryAccent: primary, onPrimary: '#FFF' };
  const trigger = isDark ? TRIGGER_ON_DARK : TRIGGER_ON_LIGHT;
  return {
    primaryAccent: adjustForContrast(primary, backgroundHex, TARGET_CONTRAST, trigger),
    onPrimary: contrast(WHITE_RGB, rgb) >= TRIGGER_ON_PRIMARY ? '#FFF' : rgbToHex(BLACK_RGB)
  };
};

// Paleta neutra del modo oscuro (independiente de la marca).
// extraLightPrimary se calcula a partir del primary de cada marca (ver darkNeutralsFor).
export const DARK_NEUTRALS = {
  background: '#0f1117',   // fondo de página
  snow: '#1a1d26',         // superficie: cards, modales, inputs
  gray: '#262a36',         // superficie secundaria: chips, hover, filas
  grayMedium: '#8a91a3',   // texto atenuado / iconos / bordes suaves
  black: '#f2f4f8',        // texto fuerte
  coal: '#d5d9e2',         // texto normal
  border: '#2e3340',       // bordes de cards e inputs
  borderStrong: '#3b4150', // bordes marcados (inputs con foco, tablas)
  surfaceMuted: '#20242e', // fondos suaves (#f5f5f5 / #fafafa en light)
  textStrong: '#f2f4f8',
  textSecondary: '#b7bdcb',
  textMuted: '#8a91a3',
  overlay: 'rgba(0, 0, 0, 0.65)',
  // Fondos suaves de estado (avisos, tags): tinte translúcido sobre superficie oscura
  tintWarning: 'rgba(250, 173, 20, 0.16)',
  tintSuccess: 'rgba(82, 196, 26, 0.16)',
  tintInfo: 'rgba(24, 144, 255, 0.16)',
  tintError: 'rgba(245, 34, 45, 0.16)'
};

const cssVarName = token => `--dsp-${token}`;

export const cssVar = token => `var(${cssVarName(token)})`;

const hexToRgba = (hex, alpha) => {
  const clean = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Neutros dark para una marca: el tinte "extraLightPrimary" se deriva del primary
// para que chips/fondos suaves sigan teniendo el color de la marca.
export const darkNeutralsFor = primary => ({
  ...DARK_NEUTRALS,
  extraLightPrimary: hexToRgba(primary, 0.18) || '#1f2b3d',
  ...deriveBrandTokens(primary, DARK_NEUTRALS.background, true)
});

export const getStoredTheme = () => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === THEME_DARK ? THEME_DARK : THEME_LIGHT;
  } catch (e) {
    return THEME_LIGHT;
  }
};

export const isDarkTheme = () => getStoredTheme() === THEME_DARK;

let lightNeutrals = null;
let darkNeutrals = null;

// Escribe las variables CSS del tema en <html>.
const writeVars = theme => {
  const root = document.documentElement;
  const values = theme === THEME_DARK ? darkNeutrals : lightNeutrals;
  if (!values) return;
  THEMED_TOKENS.forEach(token => {
    if (values[token] !== undefined) root.style.setProperty(cssVarName(token), values[token]);
  });
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme === THEME_DARK ? 'dark' : 'light';
};

// Llamado una vez desde Colors.js con los valores light de la marca activa.
export const initTheme = (light, primary) => {
  lightNeutrals = { ...light, ...deriveBrandTokens(primary, light.background || '#ffffff', false) };
  darkNeutrals = darkNeutralsFor(primary);
  writeVars(getStoredTheme());
};

export const applyTheme = theme => {
  writeVars(theme === THEME_DARK ? THEME_DARK : THEME_LIGHT);
};

export const setTheme = theme => {
  const next = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch (e) {
    // sin storage: queda solo en memoria hasta recargar
  }
  applyTheme(next);
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: next }));
  return next;
};

export const toggleTheme = () => setTheme(isDarkTheme() ? THEME_LIGHT : THEME_DARK);

// Para contextos que no entienden `var(...)` (canvas de Chart.js, Google Maps, etc.):
// devuelve el valor real del token en el tema actual.
export const resolveColor = value => {
  if (typeof value !== 'string' || !value.startsWith('var(')) return value;
  const name = value.slice(4, -1).trim();
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return resolved || value;
};
